import { API_CONFIG, fetchAPI } from './api';
import type {
  PayGradeData,
  InterviewIQData,
  InvestorIQData,
  FundingIQData,
  CompanyIntel,
} from '@/types';

interface GradeResponse<T> {
  success: boolean;
  type: string;
  result: T;
  error?: string;
}

// Calculate PayGrade
export async function calculatePayGrade(
  companyId: string,
  companyName: string,
  intel: CompanyIntel
): Promise<PayGradeData> {
  const companyData = {
    companyName,
    glassdoor: intel.glassdoor?.data,
    consumer_intel: intel.consumer_intel?.data,
    linkedin_360: intel.linkedin_360?.data,
    crunchbase: intel.crunchbase?.data,
  };

  const response = await fetchAPI<GradeResponse<PayGradeData>>(
    `${API_CONFIG.GRADES_API}/paygrade`,
    {
      method: 'POST',
      body: JSON.stringify({ companyId, companyData }),
    }
  );

  if (!response.success || !response.result) {
    throw new Error(response.error || 'Failed to calculate PayGrade');
  }

  return response.result;
}

// Calculate InterviewIQ
export async function calculateInterviewIQ(
  companyId: string,
  companyName: string,
  intel: CompanyIntel,
  contactDomain?: string
): Promise<InterviewIQData> {
  const companyData = {
    companyName,
    glassdoor: intel.glassdoor?.data,
    linkedin_360: intel.linkedin_360?.data,
    contactDomain,
  };

  const response = await fetchAPI<GradeResponse<InterviewIQData>>(
    `${API_CONFIG.GRADES_API}/interviewiq`,
    {
      method: 'POST',
      body: JSON.stringify({ companyId, companyData }),
    }
  );

  if (!response.success || !response.result) {
    throw new Error(response.error || 'Failed to calculate InterviewIQ');
  }

  return response.result;
}

// Calculate InvestorIQ
export async function calculateInvestorIQ(
  companyId: string,
  companyName: string,
  intel: CompanyIntel
): Promise<InvestorIQData> {
  const response = await fetchAPI<GradeResponse<InvestorIQData>>(
    `${API_CONFIG.GRADES_API}/investoriq`,
    {
      method: 'POST',
      body: JSON.stringify({
        company_name: companyName,
        companyData: intel.crunchbase?.data,
        companyId,
      }),
    }
  );

  if (!response.success || !response.result) {
    throw new Error(response.error || 'Failed to calculate InvestorIQ');
  }

  return response.result;
}

// Calculate FundingIQ
export async function calculateFundingIQ(
  companyId: string,
  companyName: string,
  intel: CompanyIntel
): Promise<FundingIQData> {
  const companyData = {
    crunchbase: intel.crunchbase?.data,
    glassdoor: intel.glassdoor?.data,
  };

  const response = await fetchAPI<GradeResponse<FundingIQData>>(
    `${API_CONFIG.GRADES_API}/fundingiq`,
    {
      method: 'POST',
      body: JSON.stringify({
        company_name: companyName,
        companyData,
        companyId,
      }),
    }
  );

  if (!response.success || !response.result) {
    throw new Error(response.error || 'Failed to calculate FundingIQ');
  }

  return response.result;
}

// Calculate InvestorMap
export async function calculateInvestorMap(
  companyId: string,
  companyName: string,
  intel: CompanyIntel
): Promise<InvestorIQData> {
  const companyData = {
    crunchbase: intel.crunchbase?.data,
    glassdoor: intel.glassdoor?.data,
    linkedin_360: intel.linkedin_360?.data,
    consumer_intel: intel.consumer_intel?.data,
  };

  const response = await fetchAPI<GradeResponse<InvestorIQData>>(
    `${API_CONFIG.GRADES_API}/investormap`,
    {
      method: 'POST',
      body: JSON.stringify({
        company_name: companyName,
        companyData,
        companyId,
      }),
    }
  );

  if (!response.success || !response.result) {
    throw new Error(response.error || 'Failed to calculate InvestorMap');
  }

  return response.result;
}

// Calculate FundingMap (for Capital/Funding Intelligence)
export async function calculateFundingMap(
  companyId: string,
  companyName: string,
  intel: CompanyIntel
): Promise<InvestorIQData> {
  const companyData = {
    crunchbase: intel.crunchbase?.data,
    glassdoor: intel.glassdoor?.data,
    linkedin_360: intel.linkedin_360?.data,
    consumer_intel: intel.consumer_intel?.data,
  };

  const response = await fetchAPI<GradeResponse<InvestorIQData>>(
    `${API_CONFIG.GRADES_API}/fundingmap`,
    {
      method: 'POST',
      body: JSON.stringify({
        company_name: companyName,
        companyData,
        companyId,
      }),
    }
  );

  if (!response.success || !response.result) {
    throw new Error(response.error || 'Failed to calculate FundingMap');
  }

  return response.result;
}

// Add company to Look-A-Like list
export async function addToLAL(
  userId: string,
  companyId: string,
  companyName: string,
  companyData: unknown
): Promise<void> {
  await fetchAPI(`${API_CONFIG.GRADES_API}/lal/add`, {
    method: 'POST',
    body: JSON.stringify({
      userId,
      companyId,
      companyName,
      companyData,
    }),
  });
}

// Remove company from Look-A-Like list
export async function removeFromLAL(
  userId: string,
  companyId: string
): Promise<void> {
  await fetchAPI(`${API_CONFIG.GRADES_API}/lal/remove`, {
    method: 'POST',
    body: JSON.stringify({ userId, companyId }),
  });
}

// Get user's LAL list
export async function getLALList(
  userId: string
): Promise<{ companies: unknown[] }> {
  return fetchAPI(`${API_CONFIG.GRADES_API}/lal/list?userId=${encodeURIComponent(userId)}`);
}
